import { toBoolean } from "#ce/utils/props";

/**
 * Whether a row can be the combobox's pending choice: an object row (a primitive carries neither a
 * hint nor a payload), not a header, not disabled, not withheld through `props.selectable`, and not
 * opted out through a root-level `pendingChoice: false` (a prompt row, which a click may still
 * select). A branch row is eligible only where the panel has resolved it as selectable, which it
 * records in `props.selectable`; before that resolution `markFirstResult` is told through
 * `selectableBranches`.
 *
 * @param {unknown} item
 * @returns {boolean}
 */
export function isPendingChoiceRow(item) {
  return isEligibleRow(item) && item.props?.selectable !== false;
}

// Everything the rule decides from the row itself, leaving selectability to the caller. A header is
// decided by key presence, the way sp-list decides which rows it renders as a subheader.
function isEligibleRow(item) {
  return (
    typeof item === "object" &&
    item !== null &&
    !("header" in item) &&
    item.pendingChoice !== false &&
    !toBoolean(item.disabled) &&
    !toBoolean(item.props?.disabled)
  );
}

/**
 * The row an Enter from an activator commits: the first eligible marked row while `honourMarked`,
 * otherwise the first eligible row, or null under `markedOnly`. A marked row that is not itself
 * committable is passed over. Reads the rendered list, whose branch rows already carry the panel's
 * `props.selectable`. A primitive row carries no payload, so the result is always an object or null.
 *
 * @param {unknown[]} items
 * @param {{ honourMarked: boolean, markedOnly?: boolean }} options
 * @returns {object | null}
 */
export function firstResultRow(items, { honourMarked, markedOnly = false }) {
  if (honourMarked) {
    const marked = items.find((item) => item?.props?.highlighted && isPendingChoiceRow(item));
    if (marked) {
      return marked;
    }
  }

  if (markedOnly) {
    return null;
  }

  return items.find(isPendingChoiceRow) ?? null;
}

/**
 * Marks the first eligible row as the combobox's pending choice: a copy with `props.highlighted`
 * set, which sp-list-item renders as the hover-style hint. Runs before the panel resolves branch
 * committability, so `selectableBranches` says whether a branch may be marked. A primitive row
 * cannot carry props, so such a list gets no hint.
 *
 * @param {unknown[]} items
 * @param {{ selectableBranches?: boolean }} [options]
 * @returns {unknown[]}
 */
export function markFirstResult(items, { selectableBranches = false } = {}) {
  // The panel decides a branch row's selectability from `selectableBranches` alone, overwriting
  // whatever the row carried, so the hint reads a branch the same way.
  const markable = (item) =>
    isEligibleRow(item) && (toBoolean(item.hasChildren) ? selectableBranches : item.props?.selectable !== false);
  const index = items.findIndex(markable);

  if (index === -1) {
    return items;
  }

  const first = items[index];
  const marked = [...items];
  marked[index] = { ...first, props: { ...(first.props ?? {}), highlighted: true } };
  return marked;
}
